/* Dokument: Entities mit Brushes. Alle Änderungen liefern ein neues Dokument; unveränderte Teile bleiben dasselbe Objekt. */
import type { Brush, Doc, Entity } from './types.ts';
import I18n from '../i18n.ts';
import { newId } from './ids.ts';

const _ = I18n.t;

export function newWorld(): Entity {
  return { id: newId(), kv: { classname: 'worldspawn', mapversion: '220', wad: '', message: '', skyname: 'desert', MaxRange: '4096' }, brushes: [] };
}
export function emptyDoc(): Doc { return { ents: [newWorld()] }; }

export function allBrushes(doc: Doc): { b: Brush; ei: number }[] {
  const r: { b: Brush; ei: number }[] = [];
  doc.ents.forEach((e, ei) => e.brushes.forEach((b) => r.push({ b: b, ei: ei })));
  return r;
}
export function findBrush(doc: Doc, id: string): { ei: number; bi: number; b: Brush } | null {
  for (let ei = 0; ei < doc.ents.length; ei++) {
    const bi = doc.ents[ei].brushes.findIndex((b) => b.id === id);
    if (bi >= 0) return { ei: ei, bi: bi, b: doc.ents[ei].brushes[bi] };
  }
  return null;
}
/** fn(brush) -> Brush | Brush[] | null (löschen). ids: Set. */
export function mapBrushes(doc: Doc, ids: Set<string>, fn: (b: Brush) => Brush | Brush[] | null | undefined): Doc {
  let changed = false;
  const ents = doc.ents.map((e) => {
    if (!e.brushes.some((b) => ids.has(b.id))) return e;
    const nb: Brush[] = [];
    e.brushes.forEach((b) => {
      if (!ids.has(b.id)) { nb.push(b); return; }
      const r = fn(b);
      if (r === null || r === undefined) return;
      if (Array.isArray(r)) r.forEach((x) => nb.push(x)); else nb.push(r);
    });
    changed = true;
    return Object.assign({}, e, { brushes: nb });
  });
  return changed ? { ents: ents } : doc;
}
export function addBrushes(doc: Doc, brushes: Brush[], ei?: number): Doc {
  const at = ei || 0;
  return { ents: doc.ents.map((e, i) => (i === at ? Object.assign({}, e, { brushes: e.brushes.concat(brushes) }) : e)) };
}
export function addEntity(doc: Doc, ent: Entity): Doc { return { ents: doc.ents.concat([ent]) }; }
export function updateEntity(doc: Doc, id: string, fn: (e: Entity) => Entity): Doc { return { ents: doc.ents.map((e) => (e.id === id ? fn(e) : e)) }; }
/** Brushes in neue Brush-Entity verschieben (ei=0 = zurück zur Welt). */
export function moveBrushesToEntity(doc: Doc, ids: Set<string>, targetId: string): Doc {
  const moved: Brush[] = [];
  let ents = doc.ents.map((e) => {
    const keep: Brush[] = [];
    e.brushes.forEach((b) => { if (ids.has(b.id) && e.id !== targetId) moved.push(b); else keep.push(b); });
    return keep.length === e.brushes.length ? e : Object.assign({}, e, { brushes: keep });
  });
  ents = ents.map((e) => (e.id === targetId ? Object.assign({}, e, { brushes: e.brushes.concat(moved) }) : e));
  // leere Brush-Entities (nicht Welt, keine origin) entfernen
  ents = ents.filter((e, i) => i === 0 || e.brushes.length || e.kv.origin !== undefined);
  return { ents: ents };
}
export function entityOfBrush(doc: Doc, id: string): Entity | null {
  for (const e of doc.ents) if (e.brushes.some((b) => b.id === id)) return e;
  return null;
}

/** Rückgängig/Wiederholen: Stapel unveränderlicher Dokumente. */
export class History {
  stack: { doc: Doc; label: string }[];
  i: number;
  max: number;
  constructor(doc: Doc) { this.stack = [{ doc: doc, label: _('core.hist_start') }]; this.i = 0; this.max = 200; }
  get doc(): Doc { return this.stack[this.i].doc; }
  push(doc: Doc, label?: string): boolean {
    if (doc === this.doc) return false;
    this.stack.length = this.i + 1;
    this.stack.push({ doc: doc, label: label || '' });
    if (this.stack.length > this.max) this.stack.shift(); else this.i++;
    return true;
  }
  /** Ersetzt den aktuellen Schritt (ein Strich aus vielen Einzelschritten bleibt ein Undo-Schritt) */
  replace(doc: Doc, label?: string): void { this.stack[this.i] = { doc: doc, label: label || this.stack[this.i].label }; }
  canUndo(): boolean { return this.i > 0; }
  canRedo(): boolean { return this.i < this.stack.length - 1; }
  undo(): Doc { if (this.canUndo()) this.i--; return this.doc; }
  redo(): Doc { if (this.canRedo()) this.i++; return this.doc; }
  reset(doc: Doc): void { this.stack = [{ doc: doc, label: _('core.hist_start') }]; this.i = 0; }
}
